import { NavLink, Outlet, Link } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, Home, User, Scale, FileText, FolderTree, Image, Inbox, Phone, Search, Share2, Settings, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/admin/homepage", label: "Homepage", Icon: Home },
  { to: "/admin/profile", label: "Profile", Icon: User },
  { to: "/admin/practice-areas", label: "Practice Areas", Icon: Scale },
  { to: "/admin/blog", label: "Blog Posts", Icon: FileText },
  { to: "/admin/categories", label: "Categories", Icon: FolderTree },
  { to: "/admin/media", label: "Media Library", Icon: Image },
  { to: "/admin/enquiries", label: "Enquiries", Icon: Inbox },
  { to: "/admin/contact", label: "Contact Information", Icon: Phone },
  { to: "/admin/seo", label: "SEO Settings", Icon: Search },
  { to: "/admin/social", label: "Social Links", Icon: Share2 },
  { to: "/admin/settings", label: "Site Settings", Icon: Settings },
];

export function AdminLayout() {
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-ivory-dark flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-navy flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-ivory/10">
          <span className="w-8 h-8 rounded-sm border border-brass/50 flex items-center justify-center text-brass"><Scale size={16} /></span>
          <span className="text-ivory font-serif text-sm">Admin Panel</span>
        </div>
        <nav className="flex-1 overflow-y-auto legal-scrollbar py-4 px-3 space-y-1">
          {LINKS.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brass text-navy font-semibold" : "text-ivory/70 hover:bg-navy-light hover:text-ivory"}`}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-ivory/10 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-ivory/70 hover:text-ivory"><ExternalLink size={17} /> View Website</a>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-red-300 hover:bg-red-500/10" data-testid="admin-logout"><LogOut size={17} /> Logout</button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <button className="lg:hidden text-navy" onClick={() => setOpen(!open)}>{open ? <X size={22} /> : <Menu size={22} />}</button>
          <div className="flex-1" />
          <div className="text-sm text-slate-500">{user?.email}</div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
